import { Permission } from "~/helpers/permissions.helper";
import { useUserData } from "~/store/userStore";

export const usePermissionManager = () => {
  const userData = useUserData();
  const usersPermissions =
    userData?.roles || [];
  const permissions = [...usersPermissions];

  const permissionsDict = permissions.reduce((acc: Record<string, boolean>, curr: string) => {
    acc[curr] = true;
    return acc;
  }, {});

  const hasPermission = (role: string) => {
    try {
      if (!userData) {
        return false;
      }
      const hasPermission = permissionsDict[role];
      return !!hasPermission;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const isAdmin = () => hasPermission(Permission.IS_ADMIN);
  const isInvestigador = () => hasPermission(Permission.IS_INVESTIGADOR);

  const hasReportAccess = ({ reportType }: { reportType: string }) => {
    const userReportTypesAvailable = userData?.report_types || [];
    return userReportTypesAvailable.includes(reportType);
  }

  const getCurrentPermissions = () => permissions;

  const checkPermission = (permission: Permission) => hasPermission(permission);
  /* relatórios */
  const canCreateReport = () => hasPermission(Permission.CREATE_REPORT);
  const canViewReportList = () => hasPermission(Permission.VIEW_REPORT_LIST);
  const canViewReportDetails = () => hasPermission(Permission.VIEW_REPORT_DETAILS);
  const canUpdateReport = () => hasPermission(Permission.UPDATE_REPORT);
  const canCreateReportSummary = () => hasPermission(Permission.CREATE_REPORT_SUMMARY);
  const canViewReportSummary = () => hasPermission(Permission.GET_REPORT_SUMMARY);
  const canCreatePDF = () => hasPermission(Permission.CREATE_PDF);
  const canCreateDOCX = () => hasPermission(Permission.CREATE_DOCX);
  /* convites */
  const canInviteUser = () => hasPermission(Permission.INVITE_USER);
  const canCancelInvite = () => hasPermission(Permission.CANCEL_INVITE);
  const canGetUserInvite = () => hasPermission(Permission.GET_USER_INVITE);
  const canGetDataFromUserInvite = () => hasPermission(Permission.GET_DATA_FROM_USER_INVITE);
  const canGetInviteDetails = () => hasPermission(Permission.GET_INVITE_DETAILS);
  const canAnswerInvite = () => hasPermission(Permission.ANSWER_INVITE);
  /* usuários */
  const canEditUser = () => hasPermission(Permission.EDIT_USER);
  const canGetUserLogs = () => hasPermission(Permission.GET_USER_LOGS);
  /* organizações */
  const canGetOrganizationLogs = () => hasPermission(Permission.GET_ORGANIZATION_LOGS);
  const canGetOrganizationInvite = () => hasPermission(Permission.GET_ORGANIZATION_INVITE);
  const canUpdatePrintSnapLogo = () => hasPermission(Permission.PRINT_SNAP_LOGO);
  /* api key */
  const canAddApiKey = () => hasPermission(Permission.ADD_API_KEY);
  /* criptografia organizacional — admin */
  const canSetupOrgEncryption = () => hasPermission(Permission.ORG_ENCRYPTION_SETUP);
  const canGrantOrgEncryptionAdmin = () => hasPermission(Permission.ORG_ENCRYPTION_GRANT_ADMIN);
  const canResetUserEncryptionKey = () => hasPermission(Permission.ORG_ENCRYPTION_RESET_USER_KEY);
  const canManageRecoveryKit = () => hasPermission(Permission.ORG_ENCRYPTION_RECOVERY_KIT);
  const canViewEncryptionAuditLog = () => hasPermission(Permission.ORG_ENCRYPTION_AUDIT_LOG);
  const canViewPasswordResetRequests = () => hasPermission(Permission.VIEW_PASSWORD_RESET_REQUESTS);
  /* criptografia organizacional — investigador + admin */
  const canRequestPasswordReset = () => hasPermission(Permission.REQUEST_PASSWORD_RESET);

  return {
    checkPermission,
    canCreateReport,
    canViewReportList,
    canViewReportDetails,
    canUpdateReport,
    canInviteUser,
    canCancelInvite,
    canGetUserInvite,
    canGetDataFromUserInvite,
    canGetInviteDetails,
    canAnswerInvite,
    canEditUser,
    canGetUserLogs,
    canGetOrganizationLogs,
    canGetOrganizationInvite,
    canAddApiKey,
    getCurrentPermissions,
    canUpdatePrintSnapLogo,
    isAdmin,
    isInvestigador,
    hasReportAccess,
    canCreateReportSummary,
    canViewReportSummary,
    canCreatePDF,
    canCreateDOCX,
    canSetupOrgEncryption,
    canGrantOrgEncryptionAdmin,
    canResetUserEncryptionKey,
    canManageRecoveryKit,
    canViewEncryptionAuditLog,
    canViewPasswordResetRequests,
    canRequestPasswordReset,
  };
};
